import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard, Wallet, TrendingUp, LogOut, Menu, Briefcase,
} from "lucide-react";
import WhatsAppFloat from "./WhatsAppFloat";

const nav = [
  { to: "/distributor", label: "Overview", icon: LayoutDashboard, end: true, testid: "dist-nav-overview" },
  { to: "/distributor/commissions", label: "Commissions", icon: TrendingUp, testid: "dist-nav-commissions" },
  { to: "/distributor/wallet", label: "Wallet", icon: Wallet, testid: "dist-nav-wallet" },
];

function SidebarInner({ onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2 px-5 py-6 border-b border-slate-800">
        <div className="w-9 h-9 rounded-md bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-sky-400" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">HisaabPro</div>
          <div className="text-[10px] text-sky-400 uppercase font-semibold tracking-wider -mt-0.5">Referral Partner Portal</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-sky-600/20 text-sky-300 border border-sky-600/30"
                         : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"
              }`
            }>
            <n.icon className="w-[18px] h-[18px]" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 text-[11px] text-slate-500 border-t border-slate-800">
        Referral Partner Program · HisaabPro
      </div>
    </div>
  );
}

export default function DistributorShell({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-[250px] shrink-0 fixed inset-y-0 left-0">
        <SidebarInner />
      </aside>
      <div className="flex-1 lg:ml-[250px] min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="dist-open-sidebar">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[260px]"><SidebarInner onNavigate={() => setOpen(false)} /></SheetContent>
            </Sheet>
            <div>
              <div className="text-xs text-slate-500 font-medium">Referral Partner</div>
              <div className="text-sm font-display font-semibold">{user?.shop_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200" data-testid="dist-role-badge">
              <Briefcase className="w-3 h-3" /> Partner
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="dist-user-menu" className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-100">
                  <div className="w-9 h-9 rounded-full bg-sky-600 text-white flex items-center justify-center font-semibold">
                    {(user?.owner_name || "D").charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-semibold leading-tight">{user?.owner_name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="dist-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">{children}</main>
      </div>
      <WhatsAppFloat label="WhatsApp Support" />
    </div>
  );
}
